import { MessageTypeColor } from "@/types";
import {
  botMessageColors,
  botMessageColorsDark,
} from "../../../../colors/colors";
import { useTheme } from "@mui/material/styles";
import { getTabbedContentMobilePadding } from "../../../mmk-section/styles";

export const useTabbedMessageStyles = (
  isBot: boolean,
  variant: MessageTypeColor = "default",
) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const messageColors = isBot
    ? isDark
      ? botMessageColorsDark
      : botMessageColors
    : isDark
      ? botMessageColorsDark
      : botMessageColors;

  const colors = messageColors[variant] || messageColors.default;
  const flashColors = theme.palette.flashColors;

  const tabBgColor = isDark
    ? "rgba(255,255,255,0.08)"
    : flashColors.flashBg || "#E8E0F7";
  const tabContainerBgColor = isDark
    ? tabBgColor
    : flashColors.flashBg || "#E8E0F7";
  const tabContentBgColor = isDark
    ? "#020817"
    : flashColors.flashBg || "#E8E0F7";
  const tabActiveBgColor = isDark ? flashColors.flash1 || "#2927a5" : "#6A3EE8";
  const tabActiveTextColor = isDark ? "#FFFFFF" : "#FFFFFF";
  const tabInactiveTextColor = isDark ? colors.text : "#6A3EE8";
  const titleChipBg = isDark ? "#2E3192" : "#7B68EE";
  const contentFrameBorder = isDark
    ? "1px solid rgba(255, 255, 255, 0.14)"
    : "1px solid rgba(106, 62, 232, 0.14)";
  const contentFrameShadow = isDark
    ? "0 8px 22px rgba(0, 0, 0, 0.5)"
    : "0 8px 24px rgba(68, 51, 141, 0.12)";
  // const tabBorderColor = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";

  return {
    wrapper: {
      display: "flex",
      flexDirection: "column",
      position: "relative",
      alignItems: "stretch",
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      overflowX: "hidden",
      overflowY: "visible",
      [theme.breakpoints.down("sm")]: {
        maxWidth: "100%",
        width: "100%",
        pointerEvents: "auto",
      },
    },
    tabsContainer: {
      display: "flex",
      flexDirection: "row",
      gap: "4px",
      paddingLeft: "0px",
      paddingBottom: "0px",
      marginBottom: "8px",
      overflowX: "auto",
      overflowY: "hidden",
      width: "100%",
      maxWidth: "100%",
      zIndex: 10,
      WebkitOverflowScrolling: "touch",
      scrollbarWidth: "thin",
      scrollBehavior: "smooth",
      background: tabBgColor,
      borderRadius: "12px",
      padding: "4px",
      "&::-webkit-scrollbar": {
        height: "4px",
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent",
      },
      "&::-webkit-scrollbar-thumb": {
        background: colors.text,
        borderRadius: "2px",
        opacity: 0.3,
      },
    },
    messageTitleChip: {
      display: "inline-block",
      alignSelf: "center",
      width: "fit-content",
      maxWidth: "100%",
      boxSizing: "border-box",
      flexShrink: 0,
      position: "relative",
      ml: "10px",
      mt: 0,
      mb: 0,
      px: { xs: 2.5, sm: 2.75 },
      py: { xs: 1.25, sm: 1.125 },
      borderRadius: { xs: "14px", sm: "16px" },
      backgroundColor: titleChipBg,
      color: "#FFFFFF",
      fontWeight: 700,
      fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.9375rem" },
      lineHeight: 1.35,
      whiteSpace: "normal",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
      ...(isDark
        ? {
            boxShadow:
              "0 2px 14px rgba(12, 16, 48, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.06)",
          }
        : {}),
      "&::before": {
        content: '""',
        position: "absolute",
        left: { xs: "-8px", sm: "-9px" },
        top: "50%",
        transform: "translateY(-50%)",
        width: 0,
        height: 0,
        borderTop: { xs: "6px solid transparent", sm: "7px solid transparent" },
        borderBottom: {
          xs: "6px solid transparent",
          sm: "7px solid transparent",
        },
        borderRight: `10px solid ${titleChipBg}`,
        filter: isDark
          ? "drop-shadow(0 0 6px rgba(46, 49, 146, 0.55))"
          : "drop-shadow(0 0 6px rgba(123, 104, 238, 0.4))",
      },
      "& .markdown > *": {
        margin: 0,
      },
      [theme.breakpoints.down("sm")]: {
        maxWidth: "52vw",
        height: "34px",
        minHeight: "34px",
        display: "inline-flex",
        alignItems: "center",
        paddingTop: 0,
        paddingBottom: 0,
        whiteSpace: "nowrap",
        overflow: "visible",
        "& .markdown": {
          display: "block",
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          whiteSpace: "nowrap",
          overflowWrap: "normal",
          wordBreak: "normal",
          minWidth: 0,
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
        "& .markdown > *": {
          whiteSpace: "nowrap",
          minWidth: "max-content",
        },
      },
    },
    container: {
      position: "relative",
      borderRadius: "12px",
      p: 0,
      color: colors.text,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
      display: "flex",
      flexDirection: "column",
      background: tabContainerBgColor,
      borderTop: "none",
      overflowX: "hidden",
      overflowY: "visible",
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      [theme.breakpoints.down("sm")]: {
        overflow: "visible",
        pointerEvents: "auto",
      },
    },
    tab: {
      position: "relative",
      padding: "10px 20px",
      cursor: "pointer",
      border: "none",
      background: "transparent",
      color: tabInactiveTextColor,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
      fontSize: "14px",
      whiteSpace: "nowrap",
      transition: "all 0.2s ease",
      flexShrink: 0,
      minWidth: "fit-content",
      borderRadius: "8px",
      "&:first-of-type": {
        marginLeft: "0px",
      },
      "&:hover": {
        opacity: 0.8,
      },
      [theme.breakpoints.up("sm")]: {
        marginBottom: "6px",
        marginTop: "4px",
      },
      [theme.breakpoints.down("sm")]: {
        padding: "6px 12px",
        fontSize: "12px",
      },
    },
    tabActive: {
      opacity: 1,
      fontWeight: 500,
      background: tabActiveBgColor,
      color: tabActiveTextColor,
      border: "none",
      borderRadius: "8px",
      boxShadow: isDark
        ? "0 2px 8px rgba(0, 0, 0, 0.2)"
        : "0 2px 8px rgba(106, 62, 232, 0.3)",
      position: "relative",
      overflow: "visible",
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: "-10px",
        left: "50%",
        transform: "translateX(-50%)",
        width: 0,
        height: 0,
        borderLeft: "8px solid transparent",
        borderRight: "8px solid transparent",
        borderTop: `10px solid ${tabActiveBgColor}`,
        zIndex: 11,
        pointerEvents: "none",
        [theme.breakpoints.down("sm")]: {
          borderLeft: "7px solid transparent",
          borderRight: "7px solid transparent",
          borderTop: `10px solid ${tabActiveBgColor}`,
          bottom: "-5px",
        },
      } as React.CSSProperties,
      [theme.breakpoints.down("md")]: {
        background: `${tabActiveBgColor} !important`,
        color: `${tabActiveTextColor} !important`,
        opacity: "1 !important",
      },
    },
    tabContent: {
      width: "auto",
      maxWidth: "100%",
      minWidth: 0,
      mt: "16px",
      mx: "16px",
      mb: "16px",
      pt: "20px",
      px: "20px",
      pb: "16px",
      ...getTabbedContentMobilePadding(theme),
      minHeight: "80px",
      color: colors.text,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
      background: tabContentBgColor,
      borderRadius: "12px",
      boxSizing: "border-box",
      border: contentFrameBorder,
      boxShadow: contentFrameShadow,
      [theme.breakpoints.down("sm")]: {
        mt: "12px",
        mx: "12px",
        mb: "14px",
        pt: "16px",
        px: "16px",
        pb: "14px",
      },
      "& img": {
        width: "100%",
        height: "auto",
        maxWidth: "100%",
        objectFit: "contain",
      },
    },
    loadingIndicator: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      marginTop: "8px",
      color: colors.text,
      opacity: 0.7,
      fontSize: "14px",
    },
    loadingDots: {
      display: "inline-flex",
      gap: "4px",
      "& span": {
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        background: colors.text,
        opacity: 0.5,
        animation: "pulse 1.4s ease-in-out infinite",
        "&:nth-of-type(1)": {
          animationDelay: "0s",
        },
        "&:nth-of-type(2)": {
          animationDelay: "0.2s",
        },
        "&:nth-of-type(3)": {
          animationDelay: "0.4s",
        },
      },
    },
    pendingPlaceholder: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100px",
      color: colors.text,
      opacity: 0.5,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
      fontSize: "14px",
    },
    timestamp: {
      fontSize: "12px",
      whiteSpace: "nowrap",
      alignSelf: "flex-end",
      color: colors.timeColor,
      fontFamily: "Red Hat Display",
      fontWeight: 500,
      marginTop: "4px",
      marginBottom: "4px",
    },
  };
};

// Keyframes for pulse animation
export const pulseKeyframes = {
  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 0.4,
    },
    "50%": {
      opacity: 1,
    },
  },
};
